import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import { mailTemplateDefinitions } from '@adornis/mails/db/mail-template-definition.js';
import { MailTemplateType } from '../mails/mail-templates.js';
import { assertCallerIsAdmin } from './admin-guard.js';

// MW and BH share the @adornis/mails global registry (and the same Mongo
// collection). BH's notification modules register their templates as a side
// effect of being imported from MW's server startup, so the registry contains
// BH entries too. Restrict the admin editor to MW-owned types only.
const MATCHING_WEEK_MAIL_TYPES = new Set<string>(Object.values(MailTemplateType));

export const getMatchingWeekMailTemplates = registerQuery({
  operationName: 'getMatchingWeekMailTemplates',
  type: () => [MailTemplate],
  resolve: () => async (bqlFields: BaseQLSelectionSet<MailTemplate>) => {
    await assertCallerIsAdmin();
    const existingGlobalMailTemplates = await mongo()
      .collection<EntityData<MailTemplate>>(MailTemplate._collectionName)
      .find({ _class: MailTemplate._class, emailType: { $in: [...MATCHING_WEEK_MAIL_TYPES] } })
      .toArray();

    const allTemplates = await Promise.all(
      Object.entries(mailTemplateDefinitions)
        .filter(([type]) => MATCHING_WEEK_MAIL_TYPES.has(type))
        .map(async ([type, definition]) => {
          const existingGlobalMailTemplate = existingGlobalMailTemplates.find(
            mailTemplate => mailTemplate.emailType === type,
          );
          if (existingGlobalMailTemplate) {
            existingGlobalMailTemplate.scope = 'default';
            // displayName is code-defined; keep the editor label in sync with the source.
            existingGlobalMailTemplate.name = definition.displayName;
            return existingGlobalMailTemplate;
          }

          const newMailTemplate = new MailTemplate({
            emailType: type,
            scope: 'default',
            name: definition.displayName,
            content: definition.defaultTemplate.content,
            subject: definition.defaultTemplate.subject,
            footer: definition.defaultTemplate.footer,
          });
          await newMailTemplate.create();

          return newMailTemplate;
        }),
    );

    return allTemplates;
  },
});
